<script setup lang="ts">
/** 1.a AGM Directors' Resolutions in Writing (DRW). 원본 재현. 서명 = 이사([[Signers]] = AgmSigners 위젯).
 *  (3)항의 서명 이사(ds_signatories)는 **수동 입력 가능**(이사 자동채움 + 비어있으면 서명자에서 보완).
 *  (4) 배당 결의는 **선택 항목**(declare_dividend) — 고객 통보/FS 주석에 제안배당이 있을 때만 포함.
 *      Minutes(1.d)·Notice(1.b) 와 같은 키를 공유하므로 한 번 켜면 패키지 전체가 같이 움직인다.
 *      끄면 뒤 항목 번호가 (5)→(4) 로 당겨진다. Word 템플릿도 [[if declare_dividend]]~[[endif]] 필요. */
import { computed } from 'vue';
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import AgmSigners from '../../aura/agm/cdd/AgmSigners.vue';
import { useAgmFill } from '../../aura/agm/cdd/useCddFill';
import '../../aura/agm/docs/agmDoc.css';

const fill = useAgmFill();
const dividendOn = computed(() => {
  const v = String(fill.values['declare_dividend'] ?? '').trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1';
});
function toggleDividend() { fill.setValue('declare_dividend', dividendOn.value ? 'no' : 'yes'); }
/** 배당 항목을 빼면 이후 항목 번호가 당겨진다. */
const agmItemNo = computed(() => (dividendOn.value ? 5 : 4));
</script>

<template>
  <div class="agm-doc">
    <!-- Header -->
    <div class="doc-head" style="text-align:center">
      <div class="doc-company"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div>(Incorporated in the Republic of Singapore)</div>
      <div>Unique Entity Number: <CddVarField field-key="company_uen" label="UEN" /></div>
      <div>(“the Company”)</div>
    </div>

    <p class="sec-title" style="text-align:center;margin-top:14px">DIRECTORS’ RESOLUTIONS IN WRITING PASSED PURSUANT TO THE COMPANY’S CONSTITUTION</p>

    <p class="doc-p"><b>RESOLVED –</b></p>

    <p class="doc-p">(1)&nbsp; That pursuant to Section 205C of the Singapore Companies Act 1967, the Company is a small company/group exempt from audit for its financial year ended <CddVarField field-key="financial_year_end" label="FYE" /> since it meets the requirements of not exceeding revenue/assets of $10 million and /or 50 employees for the period and in the manner stated in Section 205C of the Singapore Companies Act 1967.</p>

    <p class="doc-p">(2)&nbsp; That the Directors’ Statement and Unaudited Financial Statements of the Company for the financial year ended <CddVarField field-key="financial_year_end" label="FYE" /> be and they are hereby approved.</p>

    <p class="doc-p">(3)&nbsp; That <CddVarField field-key="ds_authorised_directors" label="Directors who sign the DS (e.g. A and B)" /> be and are hereby authorised to sign the Directors’ Statement for the financial year ended <CddVarField field-key="financial_year_end" label="FYE" /> for and on behalf of the Company.</p>

    <!-- (4) 배당 — 선택 항목. 체크했을 때만 문서에 들어가고, 해제하면 아래 항목 번호가 당겨진다. -->
    <div class="branch">
      <div class="branch-pick">
        <span class="doc-toggle" :class="{ off: !dividendOn }" @click="toggleDividend()">{{ dividendOn ? '☑' : '☐' }} Include dividend resolution (optional)</span>
      </div>
      <p class="doc-p opt" v-if="dividendOn">(4)&nbsp; That a one-tier tax exempt Final Dividend of <CddVarField field-key="dividend_per_share" label="Dividend per share" /> per ordinary share on the paid up of <CddVarField field-key="paid_up_shares" label="Paid-up shares" /> ordinary shares totaling <CddVarField field-key="dividend_total" label="Dividend total" /> be proposed in respect of the financial year ended <CddVarField field-key="financial_year_end" label="FYE" />.</p>
    </div>

    <p class="doc-p">({{ agmItemNo }})&nbsp; That the <CddVarField field-key="agm_number" label="AGM number (e.g. 20th)" /> Annual General Meeting of the Company be held on <CddVarField field-key="meeting_date" label="Meeting date" /> at <CddVarField field-key="meeting_time" label="Meeting time" /> at <CddVarField field-key="meeting_venue" label="Meeting venue" /> to approve and adopt the Unaudited Financial Statements for the financial year ended <CddVarField field-key="financial_year_end" label="FYE" /> together with the Directors’ Statement and to consider and if thought fit, to pass with or without modifications, the following resolution as ordinary resolution:</p>

    <p class="doc-p"><b>Authority to allot and issue shares</b></p>
    <p class="doc-p">“That pursuant to Section 161 of the Singapore Companies Act 1967, the Directors be and are hereby authorised to allot and issue shares in the capital of the Company to such persons at any time and upon on such terms and conditions and with such rights or restrictions as they may think fit to impose and that such authority shall continue in force until the conclusion of the next Annual General Meeting or the expiration of the period within which the next Annual General Meeting of the Company is required by law to be held, whichever is earlier.”</p>

    <!-- [[Signers]] — 이사 서명(AgmSigners 위젯). -->
    <!-- 1인 가운데 고정(패널 signBlockLayout 'resln', 고객 요청 2026-10-15) — 옵션 잠금 -->
    <AgmSigners var-key="agm_Signers" board-label="Directors" center-locked />

    <p class="doc-p">Dated this <CddVarField field-key="resolution_date" label="Resolution date" /></p>
  </div>
</template>

<style scoped>
.doc-company { font-weight: 800; font-size: 13.5px; }
.branch { margin: 4px 0; }
.branch-pick { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 4px; }
.opt { background: #f7faff; border-left: 3px solid #cfe0ff; padding-left: 8px; }
</style>
